import React, { useState, useEffect } from 'react';
import { Search, Grid, List, RefreshCw, MapPin, Calendar, TrendingUp, Users, Target, CheckCircle, XCircle } from 'lucide-react';
import './PreSowing.css';

const PreSowing = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);

  const [stats, setStats] = useState({
    totalActivities: 0,
    landPrepared: 0,
    notPrepared: 0,
    activeFarmers: 0,
    plannedArea: 0
  });

  const token = localStorage.getItem('authToken');

  // ---------------------------
  // Fetch Stats API
  // ---------------------------
  useEffect(() => {
    fetch(" https://sadaatagrofarms.com/api/crop_stages/dashboard/stats/", {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        setStats(prev => ({
          ...prev,
          totalActivities: data.total_land_preparations ?? prev.totalActivities,
          landPrepared:    data.total_land_preparations ?? prev.landPrepared,
          activeFarmers:   data.total_crops             ?? prev.activeFarmers,
          plannedArea:     data.total_crop_history      ?? prev.plannedArea,
        }));
      })
      .catch(err => console.error("Stats API Error:", err));
  }, []);

  // ---------------------------
  // Fetch Crop History Cards + Pagination
  // ---------------------------
  const loadActivities = (pageNum = 1) => {
    fetch(` https://sadaatagrofarms.com/api/crop_stages/crop-history/?page=${pageNum}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        const results = data.results || [];

        if (pageNum === 1) {
          setActivities(results);
        } else {
          setActivities(prev => [...prev, ...results]);
        }

        setNextPage(data.next);

        // ── Derive stats directly from crop-history API response ──
        const totalCount   = data.count ?? results.length;
        const prepared     = results.filter(r => r.cultivated_area_last_year > 0).length;
        const notPrepared  = results.filter(r => !r.cultivated_area_last_year || r.cultivated_area_last_year <= 0).length;
        const uniqueFarmers = [...new Set(results.map(r => r.farmer))].length;
        const totalPlanned = results.reduce((sum, r) => sum + (Number(r.planned_area_this_year) || 0), 0);

        setStats(prev => ({
          totalActivities: totalCount,
          landPrepared:    prepared,
          notPrepared:     notPrepared,
          activeFarmers:   uniqueFarmers   || prev.activeFarmers,
          plannedArea:     totalPlanned    || prev.plannedArea,
        }));
      })
      .catch(err => console.error("Card API Error:", err));
  };

  useEffect(() => {
    loadActivities(1);
  }, []);

  // Load more pagination
  const handleLoadMore = () => {
    if (nextPage) {
      const nextPageNumber = Number(nextPage.split("=")[1]);
      setPage(nextPageNumber);
      loadActivities(nextPageNumber);
    }
  };

  // ---------------------------
  // Filters Logic
  // ---------------------------
  const filteredActivities = activities.filter((item) => {
    const matchSearch =
      item.crop_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.variety_cultivated?.toLowerCase().includes(searchQuery.toLowerCase());

    const status = item.cultivated_area_last_year > 0 ? "Prepared" : "Not Prepared";
    const matchStatus = filterStatus === "All" || filterStatus === status;

    return matchSearch && matchStatus;
  });

  return (
    <div className="pre-sowing">

      {/* Stage Tabs */}
      <div className="stage-tabs">
        <button className="stage-tab active">Pre-Sowing</button>
        <button className="stage-tab">Sowing & Emergence</button>
        <button className="stage-tab">Emergence</button>
        <button className="stage-tab">Growth & Tillering</button>
        <button className="stage-tab">Booting Stage</button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Farm Activities Dashboard</h1>
          <p className="subtitle">Monitor and analyze all farm preparation activities across fields</p>
        </div>
        <button className="refresh-btn" onClick={() => loadActivities(1)}>
          <RefreshCw size={16} />
          Refresh Data
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalActivities}</div>
            <div className="stat-label">Total Activities</div>
            <div className="stat-sublabel">{stats.totalActivities} fields</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.landPrepared}</div>
            <div className="stat-label">Land Prepared</div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: stats.totalActivities > 0
                    ? `${(stats.landPrepared / stats.totalActivities) * 100}%`
                    : '0%'
                }}
              />
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red"><XCircle size={24} /></div>
          <div className="stat-content">
            <div className={`stat-value${stats.notPrepared > 0 ? ' danger' : ''}`}>
              {stats.notPrepared}
            </div>
            <div className="stat-label">Not Prepared</div>
            <div className="stat-sublabel danger">
              {stats.notPrepared > 0 ? 'Requires attention' : 'All prepared'}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue"><Users size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeFarmers}</div>
            <div className="stat-label">Active Farmers</div>
            <div className="stat-sublabel">Managing fields</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple"><Target size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.plannedArea} ha</div>
            <div className="stat-label">Planned Area</div>
            <div className="stat-sublabel">This season</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search activities, crops, fields, or farmers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select className="field-filter">
          <option>All Fields</option>
          <option>Field 1</option>
          <option>Field 2</option>
        </select>

        <div className="view-toggle">
          <button className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
            <Grid size={18} />
          </button>
          <button className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
            <List size={18} />
          </button>
        </div>

        <div className="status-filters">
          <button className={`filter-chip ${filterStatus === 'All' ? 'active' : ''}`} onClick={() => setFilterStatus('All')}>
            All
          </button>
          <button className={`filter-chip ${filterStatus === 'Prepared' ? 'active' : ''}`} onClick={() => setFilterStatus('Prepared')}>
            Prepared
          </button>
          <button className={`filter-chip ${filterStatus === 'Not Prepared' ? 'active' : ''}`} onClick={() => setFilterStatus('Not Prepared')}>
            Not Prepared
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-bar">
        <div className="results-count">
          Showing <strong>{filteredActivities.length}</strong> of <strong>{stats.totalActivities}</strong> activities
        </div>
      </div>

      {/* Activities Grid */}
      <div className={`activities-${viewMode}`}>
        {filteredActivities.map((activity) => {
          const status = activity.cultivated_area_last_year > 0 ? "Prepared" : "Not Prepared";

          return (
            <div key={activity.id} className="activity-card">
              <div className="activity-header">
                <div className="activity-crop">
                  <span className="crop-icon">🌾</span>
                  <span className="crop-name">{activity.crop_name}</span>
                </div>
                <span className={`status-badge ${status === "Prepared" ? "prepared" : "not-prepared"}`}>
                  {status}
                </span>
              </div>

              <div className="activity-location">
                <MapPin size={14} />
                <span>Farmer #{activity.farmer}</span>
              </div>

              <div className="activity-type">
                Crop History
              </div>

              <div className="activity-details">
                <div className="detail-row">
                  <span className="detail-label">Variety</span>
                  <span className="detail-value">{activity.variety_cultivated}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Avg Yield</span>
                  <span className="detail-value">{activity.average_yield}</span>
                </div>
              </div>

              <div className="activity-footer">
                <div className="activity-date">
                  <Calendar size={14} />
                  <span>Planned</span>
                  <strong>{activity.planned_area_this_year}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Button */}
      {nextPage && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button className="load-more-btn" onClick={handleLoadMore}>Load More</button>
        </div>
      )}

    </div>
  );
};

export default PreSowing;